const normalizeLanguageCode = language => {
  if (typeof language !== "string") return ""

  return language.trim().toLowerCase().split(/[-_]/)[0]
}

const languageSwitcherConfig = window.themeLanguageSwitcherConfig || {}
const defaultLang = normalizeLanguageCode(languageSwitcherConfig.defaultLanguage) || "en"
const configuredLanguages = Array.isArray(languageSwitcherConfig.availableLanguages)
  ? languageSwitcherConfig.availableLanguages
      .map(normalizeLanguageCode)
      .filter(language => language && language !== defaultLang)
  : []
const maxLanguageSwitcherAttempts = 60
const languageSwitcherRetryDelay = 250
const pathLanguageSyncDuration = 30000
const defaultLanguagePathSyncDuration = 10000
const pathLanguageSyncIntervalDelay = 250
const pathLanguageRecoveryReloadDelay = 5000
const pathLanguageRecoveryReloadCooldown = 30000
const preferredLanguageStorageKey = "preferred-language"
const languageRecoveryStoragePrefix = "language-switcher-recovery"
const weglotLanguageCookieName = "wglang"
const marketRedirectCookieName = "jones-market-target-path"
const internationalPathSuffix = "int"
let languageSwitcherRetryTimeoutId = null
let pathLanguageSyncIntervalId = null
let lastLanguageRecoveryReload = {
  key: "",
  time: 0,
}
let lastWeglotSwitchRequest = {
  language: "",
  time: 0,
}
let hasInitializedWeglot = false

const getKnownLanguages = () => {
  const knownLanguages = new Set([defaultLang])

  configuredLanguages.forEach(language => knownLanguages.add(language))
  ;[document.documentElement.lang, document.body?.lang].forEach(language => {
    const normalizedLanguage = normalizeLanguageCode(language)

    if (normalizedLanguage) knownLanguages.add(normalizedLanguage)
  })

  document
    .querySelectorAll(".language-selector a[lang], localization-form a[data-value], a[hreflang]")
    .forEach(link => {
      const normalizedLanguage = normalizeLanguageCode(
        link.getAttribute("lang") || link.dataset.value || link.getAttribute("hreflang")
      )

      if (normalizedLanguage) knownLanguages.add(normalizedLanguage)
    })

  return knownLanguages
}

const getEnabledLanguages = () => {
  const enabledLanguages = new Set(getKnownLanguages())

  document.querySelectorAll(".language-selector a[lang]").forEach(link => {
    const language = normalizeLanguageCode(link.getAttribute("lang"))

    if (language) enabledLanguages.add(language)
  })

  document.querySelectorAll("localization-form a[data-value]").forEach(link => {
    const language = normalizeLanguageCode(link.dataset.value)

    if (language) enabledLanguages.add(language)
  })

  return enabledLanguages
}

const getPathPrefix = (path = window.location.pathname) => {
  const [, firstSegment = ""] = path.split("/")
  const normalizedSegment = firstSegment.toLowerCase()
  const [segmentLanguage = "", segmentSuffix = ""] = normalizedSegment.split("-")
  const pathLanguage = normalizeLanguageCode(segmentLanguage)
  const internationalLanguageSegment = `${pathLanguage}-${internationalPathSuffix}`

  if (!getKnownLanguages().has(pathLanguage)) {
    return { language: "", segment: "", suffix: "" }
  }

  if (normalizedSegment !== pathLanguage && normalizedSegment !== internationalLanguageSegment) {
    return { language: "", segment: "", suffix: "" }
  }

  return {
    language: pathLanguage,
    segment: firstSegment,
    suffix: segmentSuffix === internationalPathSuffix ? segmentSuffix : "",
  }
}

const getPathLanguage = (path = window.location.pathname) => getPathPrefix(path).language

const getLanguageFromPath = () => getPathLanguage() || defaultLang

const hasLanguagePrefix = (path = window.location.pathname) => getPathLanguage(path) !== ""

const isDefaultLanguagePath = () => !hasLanguagePrefix() && getLanguageFromPath() === defaultLang

const stripLanguagePrefix = (path = window.location.pathname) => {
  const pathPrefix = getPathPrefix(path)

  if (!pathPrefix.language) return path || "/"

  const pathSegments = path.split("/")

  pathSegments.splice(1, 1)

  const strippedPath = pathSegments.join("/")

  return strippedPath || "/"
}

const isSupportedLanguage = language => getKnownLanguages().has(normalizeLanguageCode(language))

const isEnabledLanguage = language => getEnabledLanguages().has(normalizeLanguageCode(language))

const getStoredLanguagePreference = () => {
  try {
    const storedLanguage = normalizeLanguageCode(
      window.localStorage.getItem(preferredLanguageStorageKey)
    )

    return isSupportedLanguage(storedLanguage) ? storedLanguage : null
  } catch {
    return null
  }
}

const setStoredLanguagePreference = language => {
  const normalizedLanguage = normalizeLanguageCode(language)

  if (!isSupportedLanguage(normalizedLanguage)) return

  try {
    window.localStorage.setItem(preferredLanguageStorageKey, normalizedLanguage)
    window.localStorage.setItem(weglotLanguageCookieName, normalizedLanguage)
  } catch {}

  setWeglotLanguageCookie(normalizedLanguage)
}

const resetStoredLanguagePreferenceToDefault = () => {
  try {
    window.localStorage.setItem(preferredLanguageStorageKey, defaultLang)
    window.localStorage.setItem(weglotLanguageCookieName, defaultLang)
  } catch {}
}

const syncStoredLanguagePreferenceWithPath = language => {
  const normalizedLanguage = normalizeLanguageCode(language || getLanguageFromPath())

  if (!isSupportedLanguage(normalizedLanguage)) return

  if (hasLanguagePrefix()) {
    try {
      window.localStorage.setItem(preferredLanguageStorageKey, normalizedLanguage)
      window.localStorage.setItem(weglotLanguageCookieName, normalizedLanguage)
    } catch {}

    return
  }

  if (normalizedLanguage === defaultLang && isDefaultLanguagePath())
    resetStoredLanguagePreferenceToDefault()
}

const syncLanguageStateWithPath = () => {
  const targetLanguage = getLanguageFromPath()

  setWeglotLanguageCookie(targetLanguage)
  syncStoredLanguagePreferenceWithPath(targetLanguage)

  return targetLanguage
}

const setWeglotLanguageCookie = language => {
  const normalizedLanguage = normalizeLanguageCode(language)

  if (!isSupportedLanguage(normalizedLanguage)) return

  clearConflictingWeglotLanguageCookies(normalizedLanguage)
  setSharedCookie(weglotLanguageCookieName, normalizedLanguage)

  const pathPrefix = getPathPrefix()

  if (pathPrefix.segment) {
    setSharedCookie(
      weglotLanguageCookieName,
      normalizedLanguage,
      undefined,
      `/${pathPrefix.segment}`
    )
  }
}

const isJonesHost = hostname =>
  hostname === "jonessnowboards.com" || hostname.endsWith(".jonessnowboards.com")

const setSharedCookie = (name, value, maxAge, path = "/") => {
  try {
    const cookiePath = typeof path === "string" && path.charAt(0) === "/" ? path : "/"
    let cookieValue = `${name}=${encodeURIComponent(value)}; path=${cookiePath}; SameSite=Lax`

    if (typeof maxAge === "number") cookieValue += `; max-age=${maxAge}`

    document.cookie = cookieValue

    if (isJonesHost(window.location.hostname)) {
      document.cookie = `${cookieValue}; domain=.jonessnowboards.com`
    }
  } catch {}
}

const clearSharedCookie = (name, path = "/") => {
  setSharedCookie(name, "", 0, path)
}

const getWeglotLanguageCookiePaths = () => {
  const cookiePaths = new Set(["/"])
  const pathPrefix = getPathPrefix()

  if (pathPrefix.segment) cookiePaths.add(`/${pathPrefix.segment}`)

  getKnownLanguages().forEach(language => {
    if (language === defaultLang) return

    cookiePaths.add(`/${language}`)
    cookiePaths.add(`/${language}-${internationalPathSuffix}`)
  })

  return Array.from(cookiePaths)
}

const clearConflictingWeglotLanguageCookies = language => {
  const normalizedLanguage = normalizeLanguageCode(language)

  if (normalizedLanguage !== defaultLang || hasLanguagePrefix()) return

  getWeglotLanguageCookiePaths().forEach(path =>
    clearSharedCookie(weglotLanguageCookieName, path)
  )
}

const getUrlMarketSuffix = url => {
  const pathPrefix = getPathPrefix(url.pathname)

  return pathPrefix.suffix
}

const initMarketRedirectTracking = () => {
  if (document.body?.dataset.marketRedirectTrackingInit === "true") return
  if (!document.body) return

  document.body.dataset.marketRedirectTrackingInit = "true"

  document.querySelectorAll(".market-selector a[href]").forEach(link => {
    link.addEventListener("click", () => {
      try {
        const url = new URL(link.href, window.location.origin)

        if (!isJonesHost(url.hostname)) {
          clearSharedCookie(marketRedirectCookieName)
          return
        }

        if (getUrlMarketSuffix(url) === internationalPathSuffix) {
          setSharedCookie(
            marketRedirectCookieName,
            `${url.pathname}${url.search}${url.hash}`,
            120
          )
        } else {
          clearSharedCookie(marketRedirectCookieName)
        }
      } catch {
        clearSharedCookie(marketRedirectCookieName)
      }
    })
  })
}

const requestWeglotLanguageSwitch = (language, force = false) => {
  const normalizedLanguage = normalizeLanguageCode(language)
  const now = Date.now()

  if (!isSupportedLanguage(normalizedLanguage)) return
  if (!isWeglotReady()) return

  if (
    !force &&
    lastWeglotSwitchRequest.language === normalizedLanguage &&
    now - lastWeglotSwitchRequest.time < 1000
  ) {
    return
  }

  lastWeglotSwitchRequest = {
    language: normalizedLanguage,
    time: now,
  }

  Weglot.switchTo(normalizedLanguage)
}

const isWeglotLoaded = () => typeof Weglot !== "undefined" && typeof Weglot.on === "function"

const isWeglotReady = () =>
  typeof Weglot !== "undefined" &&
  Weglot.initialized === true &&
  typeof Weglot.switchTo === "function"

const initializeWeglot = () => {
  if (typeof Weglot === "undefined") return false

  if (Weglot.initialized === true) {
    hasInitializedWeglot = true
    return true
  }

  if (hasInitializedWeglot) return true
  if (typeof Weglot.initialize !== "function") return false

  const settings = window.themeWeglotSettings

  if (!settings || !settings.api_key) return false

  try {
    Weglot.initialize(settings)
    hasInitializedWeglot = true
  } catch {
    hasInitializedWeglot = false
    return false
  }

  return true
}

const getCurrentWeglotLanguage = () =>
  isWeglotReady() && typeof Weglot.getCurrentLang === "function"
    ? normalizeLanguageCode(String(Weglot.getCurrentLang()))
    : ""

const getLanguageRecoveryStorageKey = language => {
  const normalizedLanguage = normalizeLanguageCode(language)

  return `${languageRecoveryStoragePrefix}:${window.location.pathname}${window.location.search}:${normalizedLanguage}`
}

const getLanguageRecoveryReloadStorageKey = language =>
  `${getLanguageRecoveryStorageKey(language)}:reloaded`

const clearLanguageRecoveryReload = language => {
  try {
    window.sessionStorage.removeItem(getLanguageRecoveryStorageKey(language))
    window.sessionStorage.removeItem(getLanguageRecoveryReloadStorageKey(language))
  } catch {}
}

const recoverStuckLanguageSwitch = language => {
  const normalizedLanguage = normalizeLanguageCode(language)
  const currentWeglotLanguage = getCurrentWeglotLanguage()

  if (!isSupportedLanguage(normalizedLanguage)) return false
  if (!isWeglotReady()) return false
  if (!currentWeglotLanguage || currentWeglotLanguage === normalizedLanguage) return false
  if (normalizedLanguage === defaultLang && isDefaultLanguagePath()) return false
  if (document.visibilityState && document.visibilityState !== "visible") return false
  if (!hasLanguagePrefix()) return false

  try {
    if (window.sessionStorage.getItem(getLanguageRecoveryReloadStorageKey(normalizedLanguage))) {
      return false
    }
  } catch {}

  const recoveryStorageKey = getLanguageRecoveryStorageKey(normalizedLanguage)
  const now = Date.now()

  if (
    lastLanguageRecoveryReload.key === recoveryStorageKey &&
    now - lastLanguageRecoveryReload.time < pathLanguageRecoveryReloadCooldown
  ) {
    return false
  }

  try {
    const lastReload = Number(window.sessionStorage.getItem(recoveryStorageKey) || 0)

    if (lastReload && now - lastReload < pathLanguageRecoveryReloadCooldown) return false

    window.sessionStorage.setItem(recoveryStorageKey, String(now))
  } catch {}

  lastLanguageRecoveryReload = {
    key: recoveryStorageKey,
    time: now,
  }

  setStoredLanguagePreference(normalizedLanguage)
  setWeglotLanguageCookie(normalizedLanguage)
  try {
    window.sessionStorage.setItem(getLanguageRecoveryReloadStorageKey(normalizedLanguage), "1")
  } catch {}
  window.location.reload()

  return true
}

const getBrowserLanguage = () => {
  const browserLanguages =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language]

  for (const language of browserLanguages) {
    const normalizedLanguage = normalizeLanguageCode(language)

    if (normalizedLanguage && isEnabledLanguage(normalizedLanguage)) return normalizedLanguage
  }

  return defaultLang
}

const buildLocalizedUrl = language => {
  const normalizedLanguage = normalizeLanguageCode(language)
  const currentPathPrefix = getPathPrefix()
  const normalizedPath = stripLanguagePrefix(window.location.pathname)
  const basePath = normalizedPath === "/" ? "" : normalizedPath

  if (currentPathPrefix.suffix) {
    return `/${normalizedLanguage}-${currentPathPrefix.suffix}${basePath}${window.location.search}${window.location.hash}`
  }

  const localizedPath =
    normalizedLanguage === defaultLang ? basePath || "/" : `/${normalizedLanguage}${basePath}`

  return `${localizedPath}${window.location.search}${window.location.hash}`
}

const autoRedirectLanguageFromBrowser = () => {
  if (hasLanguagePrefix()) return false
  if (isDefaultLanguagePath()) {
    resetStoredLanguagePreferenceToDefault()
    return false
  }

  const preferredLanguage = getStoredLanguagePreference() || getBrowserLanguage()

  if (preferredLanguage === defaultLang) return false
  if (!isEnabledLanguage(preferredLanguage)) return false

  setStoredLanguagePreference(preferredLanguage)
  window.location.replace(buildLocalizedUrl(preferredLanguage))

  return true
}

const syncWeglotWithPathLanguage = () => {
  if (!isWeglotReady()) return false

  const targetLanguage = syncLanguageStateWithPath()
  const currentWeglotLanguage = getCurrentWeglotLanguage()

  if (currentWeglotLanguage === targetLanguage) return true

  requestWeglotLanguageSwitch(targetLanguage)

  return false
}

// Weglot can restore a previous language after init, so keep the URL prefix authoritative briefly.
const startPathLanguageSyncWatchdog = (duration = pathLanguageSyncDuration) => {
  if (pathLanguageSyncIntervalId !== null) {
    window.clearInterval(pathLanguageSyncIntervalId)
    pathLanguageSyncIntervalId = null
  }

  const startedAt = Date.now()

  const stopWatchdog = () => {
    if (pathLanguageSyncIntervalId === null) return

    window.clearInterval(pathLanguageSyncIntervalId)
    pathLanguageSyncIntervalId = null
  }

  const checkPathLanguage = () => {
    const elapsedTime = Date.now() - startedAt
    const targetLanguage = syncLanguageStateWithPath()
    const syncDuration =
      targetLanguage === defaultLang
        ? Math.min(duration, defaultLanguagePathSyncDuration)
        : duration

    if (elapsedTime > syncDuration) {
      stopWatchdog()
      return
    }

    if (!isWeglotReady()) return

    const currentWeglotLanguage = getCurrentWeglotLanguage()

    if (currentWeglotLanguage === targetLanguage) {
      clearLanguageRecoveryReload(targetLanguage)
      return
    }

    if (
      elapsedTime > pathLanguageRecoveryReloadDelay &&
      recoverStuckLanguageSwitch(targetLanguage)
    ) {
      return
    }

    requestWeglotLanguageSwitch(targetLanguage, true)
  }

  checkPathLanguage()

  pathLanguageSyncIntervalId = window.setInterval(
    checkPathLanguage,
    pathLanguageSyncIntervalDelay
  )
}

const initLanguageSwitcher = (attempt = 0) => {
  if (languageSwitcherRetryTimeoutId !== null) {
    window.clearTimeout(languageSwitcherRetryTimeoutId)
    languageSwitcherRetryTimeoutId = null
  }

  if (autoRedirectLanguageFromBrowser()) return
  if (syncWeglotWithPathLanguage()) return
  if (attempt >= maxLanguageSwitcherAttempts) return

  languageSwitcherRetryTimeoutId = window.setTimeout(
    () => initLanguageSwitcher(attempt + 1),
    languageSwitcherRetryDelay
  )
}

const initWeglotLanguageEvents = () => {
  initializeWeglot()

  if (!isWeglotLoaded()) return false
  if (window.__languageSwitcherWeglotEventsInit === true) return true

  window.__languageSwitcherWeglotEventsInit = true

  Weglot.on("languageChanged", language => {
    const targetLanguage = syncLanguageStateWithPath()
    const changedLanguage = normalizeLanguageCode(language) || getCurrentWeglotLanguage()

    if (changedLanguage && changedLanguage !== targetLanguage) {
      requestWeglotLanguageSwitch(targetLanguage, true)
    }

    window.setTimeout(() => {
      initLanguageSwitcher()
      startPathLanguageSyncWatchdog()
    }, 0)
  })
  Weglot.on("switchersReady", () => {
    window.setTimeout(() => {
      initLanguageSwitcher()
      startPathLanguageSyncWatchdog()
    }, 0)
  })

  if (Weglot.initialized) {
    window.setTimeout(() => {
      initLanguageSwitcher()
      startPathLanguageSyncWatchdog()
    }, 0)
  } else {
    Weglot.on("initialized", () => {
      window.setTimeout(() => {
        initLanguageSwitcher()
        startPathLanguageSyncWatchdog()
      }, 0)
    })
  }

  return true
}

const initCountrySelectorAccordion = () => {
  const dialog = document.querySelector("#Dialog-Country-Selector")

  if (!dialog || dialog.dataset.selectorAccordionInit === "true") return

  dialog.dataset.selectorAccordionInit = "true"

  const selectors = dialog.querySelectorAll(
    ".market-selector details, .country-selector details, .language-selector details"
  )

  selectors.forEach(selector => {
    selector.addEventListener("toggle", () => {
      if (!selector.open) return

      selectors.forEach(otherSelector => {
        if (otherSelector !== selector) otherSelector.removeAttribute("open")
      })
    })
  })
}

const initLanguagePreferenceTracking = () => {
  if (document.body?.dataset.languagePreferenceTrackingInit === "true") return
  if (!document.body) return

  document.body.dataset.languagePreferenceTrackingInit = "true"

  document.querySelectorAll(".language-selector a[lang]").forEach(link => {
    link.addEventListener("click", () => {
      setStoredLanguagePreference(link.getAttribute("lang"))
    })
  })

  document.querySelectorAll("localization-form a[data-value]").forEach(link => {
    link.addEventListener("click", () => {
      setStoredLanguagePreference(link.dataset.value)
    })
  })
}

syncLanguageStateWithPath()
initWeglotLanguageEvents()
startPathLanguageSyncWatchdog()

document.addEventListener("DOMContentLoaded", initLanguageSwitcher)
document.addEventListener("DOMContentLoaded", () => startPathLanguageSyncWatchdog())
document.addEventListener("DOMContentLoaded", initCountrySelectorAccordion)
document.addEventListener("DOMContentLoaded", initLanguagePreferenceTracking)
document.addEventListener("DOMContentLoaded", initMarketRedirectTracking)
document.addEventListener("DOMContentLoaded", initWeglotLanguageEvents)
window.addEventListener("load", () => {
  initLanguageSwitcher()
  startPathLanguageSyncWatchdog()
})
window.addEventListener("load", () => initWeglotLanguageEvents())
window.addEventListener("pageshow", () => {
  initLanguageSwitcher()
  startPathLanguageSyncWatchdog()
})
window.addEventListener("pageshow", () => initWeglotLanguageEvents())
window.addEventListener("focus", () => {
  initLanguageSwitcher()
  startPathLanguageSyncWatchdog(5000)
})
window.addEventListener("popstate", () => {
  initLanguageSwitcher()
  startPathLanguageSyncWatchdog()
})
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    initLanguageSwitcher()
    startPathLanguageSyncWatchdog(5000)
  }
})
setTimeout(initLanguageSwitcher, 100)
setTimeout(initLanguageSwitcher, 300)
setTimeout(initLanguageSwitcher, 1000)
setTimeout(initLanguageSwitcher, 2000)
setTimeout(initLanguageSwitcher, 3500)
setTimeout(initLanguageSwitcher, 5000)
setTimeout(initLanguageSwitcher, 8000)
setTimeout(initWeglotLanguageEvents, 100)
setTimeout(initWeglotLanguageEvents, 300)
setTimeout(startPathLanguageSyncWatchdog, 100)
setTimeout(startPathLanguageSyncWatchdog, 1000)
setTimeout(initCountrySelectorAccordion, 100)
setTimeout(initLanguagePreferenceTracking, 100)
setTimeout(initMarketRedirectTracking, 100)
